import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type PaginationState, type Table as TanStackTable } from '@tanstack/react-table';
import { type MouseEvent, useId, useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/Pagination';

import { DATA_TABLE_PAGE_SIZE_OPTIONS } from './constants';
import type { DataTableFeatures } from './dataTableFeatures';

type PaginationToken = number | 'ellipsis';

type DataTablePaginationProps<TData extends object> = {
  table: TanStackTable<DataTableFeatures, TData>;
  paginationState?: PaginationState;
  rowCount?: number;
  loading: boolean;
  error?: Error | null | undefined;
  paginationEnabled: boolean;
};

const addPageRange = (pageSet: Set<number>, start: number, end: number, pageCount: number) => {
  for (let page = start; page <= end; page += 1) {
    if (page >= 1 && page <= pageCount) {
      pageSet.add(page);
    }
  }
};

const getPaginationTokens = (pageCount: number, activePage: number): PaginationToken[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, pageCount]);

  addPageRange(pages, activePage - 2, activePage + 2, pageCount);

  if (activePage <= 3) {
    addPageRange(pages, 1, 5, pageCount);
  }

  if (activePage >= pageCount - 2) {
    addPageRange(pages, pageCount - 4, pageCount, pageCount);
  }

  const sortedPages = Array.from(pages).sort((a, b) => a - b);
  const tokens: PaginationToken[] = [];

  for (let index = 0; index < sortedPages.length; index += 1) {
    const page = sortedPages[index];
    const previousPage = sortedPages[index - 1];

    if (previousPage != null && page - previousPage > 1) {
      tokens.push('ellipsis');
    }

    tokens.push(page);
  }

  return tokens;
};

const DataTablePagination = <TData extends object>({
  table,
  paginationState,
  rowCount,
  loading,
  error,
  paginationEnabled,
}: DataTablePaginationProps<TData>) => {
  const rowsPerPageSelectId = useId();
  const pageCount = paginationEnabled ? table.getPageCount() : 0;
  const pageIndex = paginationState?.pageIndex ?? 0;
  const activePage = paginationEnabled ? pageIndex + 1 : 1;
  const currentPageSize = paginationEnabled
    ? (paginationState?.pageSize ?? DATA_TABLE_PAGE_SIZE_OPTIONS[0])
    : DATA_TABLE_PAGE_SIZE_OPTIONS[0];
  const paginationTokens = paginationEnabled ? getPaginationTokens(pageCount, activePage) : [];
  const totalResults = paginationEnabled
    ? (rowCount ?? table.getPrePaginatedRowModel().rows.length)
    : table.getRowModel().rows.length;
  // Derive from `paginationState` (same source as `activePage`), not `table.getCan*Page()`, which
  // can read stale `table.options.state.pagination` after `onPaginationChange` updates parent state.
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageCount === -1 ? true : pageCount === 0 ? false : pageIndex < pageCount - 1;
  const pageSizeOptions = useMemo(() => {
    const allOptions = new Set<number>([...DATA_TABLE_PAGE_SIZE_OPTIONS, currentPageSize]);
    return Array.from(allOptions).sort((left, right) => left - right);
  }, [currentPageSize]);
  const showPageSizeSelector = paginationEnabled && !loading && !error && totalResults > 50;
  const showPagination = paginationEnabled && !loading && !error && pageCount > 1;

  const handlePreviousPageClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (!canPreviousPage) {
      return;
    }

    table.previousPage();
  };

  const handleNextPageClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (!canNextPage) {
      return;
    }

    table.nextPage();
  };

  const handlePageClick = (event: MouseEvent<HTMLAnchorElement>, page: number) => {
    event.preventDefault();
    table.setPageIndex(page - 1);
  };

  const handlePageSizeChange = (value: number | null) => {
    if (value == null) {
      return;
    }

    table.setPagination((current) => {
      if (current.pageIndex === 0 && current.pageSize === value) {
        return current;
      }

      return {
        pageIndex: 0,
        pageSize: value,
      };
    });
  };

  if (!showPageSizeSelector && !showPagination) {
    return null;
  }

  return (
    <div className="flex justify-end gap-4 whitespace-nowrap">
      {showPagination && (
        <Pagination className="mx-0 w-auto" data-testid="c99-pagination">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={handlePreviousPageClick}
                disabled={!canPreviousPage}
              />
            </PaginationItem>
            {paginationTokens.map((token, index) => {
              if (token === 'ellipsis') {
                return (
                  <PaginationItem key={`ellipsis-${index}`}>
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }

              return (
                <PaginationItem key={token}>
                  <PaginationLink
                    href="#"
                    isActive={token === activePage}
                    onClick={(event) => handlePageClick(event, token)}
                    aria-label={t`Go to page ${token}`}
                  >
                    {token}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext href="#" onClick={handleNextPageClick} disabled={!canNextPage} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
      {showPageSizeSelector && (
        <Select size="medium" value={currentPageSize} onValueChange={handlePageSizeChange}>
          <SelectTrigger id={rowsPerPageSelectId} className="w-fit">
            <SelectValue placeholder={t`Rows per page`}>
              {(value: number) => <Trans>{value} / page</Trans>}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {pageSizeOptions.map((option) => (
                <SelectItem className="whitespace-nowrap" key={option} value={option}>
                  <Trans>{option} / page</Trans>
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export default DataTablePagination;
